import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowLeft, Send, CheckCircle2, Loader2, Download, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function CostruisciApp() {
  const { id } = useParams();
  const nav = useNavigate();
  const [appData, setAppData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [iterText, setIterText] = useState("");
  const [iterating, setIterating] = useState(false);
  const pollRef = useRef(null);
  const endRef = useRef(null);

  const load = () => api.get(`/custom-apps/${id}`).then((r) => {
    setAppData(r.data);
    setMessages(r.data.messages || []);
  });

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => {
      if (["building", "drafting_need"].includes(appData?.status)) load();
    }, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [id, appData?.status]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || sending) return;
    const t = text;
    setText("");
    setMessages((m) => [...m, { role: "user", content: t, _local: true }]);
    setSending(true);
    try {
      await api.post(`/custom-apps/${id}/messages`, { content: t });
      await load();
    } catch (err) {
      toast.error("Errore");
    } finally {
      setSending(false);
    }
  };

  const confirmNeed = async () => {
    setConfirming(true);
    try {
      await api.post(`/custom-apps/${id}/confirm-need`);
      toast.success("Costruzione avviata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setConfirming(false);
    }
  };

  const iterate = async (e) => {
    e?.preventDefault();
    if (!iterText.trim()) return;
    setIterating(true);
    try {
      await api.post(`/custom-apps/${id}/iterate`, { content: iterText });
      setIterText("");
      toast.success("Modifica in lavorazione");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setIterating(false);
    }
  };

  const download = async () => {
    const token = localStorage.getItem("adriai_token");
    const r = await fetch(`${API}/custom-apps/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Pacchetto non pronto"); return; }
    const b = await r.blob();
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u; a.download = appData.package_file; a.click();
    URL.revokeObjectURL(u);
  };

  const openPreview = () => {
    const token = localStorage.getItem("adriai_token");
    fetch(`${API}/custom-apps/${id}/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const w = window.open();
        w.document.open(); w.document.write(html); w.document.close();
      });
  };

  if (!appData) return <div className="adr-skeleton h-96" />;

  const status = appData.status;

  return (
    <div className="max-w-5xl">
      <Link to="/costruisci/le-mie" className="inline-flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#0F3D6B] mb-6">
        <ArrowLeft className="w-4 h-4" /> Le mie applicazioni
      </Link>

      <h1 className="serif text-2xl font-semibold tracking-tight mb-1" data-testid="ffa-title">{appData.title}</h1>
      <div className="text-xs text-[#555555] mb-6">Stato: <span className="capitalize font-semibold">{status.replace("_", " ")}</span> · v{appData.current_version || 0}</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CHAT */}
        <div className="adr-card p-5 flex flex-col" style={{ minHeight: 520 }}>
          <div className="adr-overline mb-3">Conversazione</div>
          <div className="flex-1 overflow-y-auto space-y-3 mb-3" style={{ maxHeight: 460 }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={m.role === "user" ? "adr-chat-bubble-user text-sm" : "adr-chat-bubble-bot text-sm"} data-testid={`ffa-msg-${i}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && <div className="text-xs text-[#555555] italic">L'agente sta pensando...</div>}
            <div ref={endRef} />
          </div>
          {status === "drafting_need" && (
            <form onSubmit={send} className="relative">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Rispondi all'agente..." className="adr-input pr-12" data-testid="ffa-input" />
              <button type="submit" disabled={sending || !text.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-md text-white flex items-center justify-center disabled:opacity-40" style={{ background: "#C8102E" }}>
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>

        {/* DOC / PREVIEW PANEL */}
        <div className="space-y-4">
          {appData.need_document && (
            <div className="adr-card p-5">
              <div className="adr-overline mb-3">Documento di Bisogno</div>
              <div className="prose prose-sm max-w-none text-[#1A1A1A] whitespace-pre-wrap text-sm leading-relaxed" data-testid="ffa-need-doc">
                {appData.need_document}
              </div>
              {status === "awaiting_confirmation" && (
                <div className="mt-4 flex gap-2">
                  <button onClick={confirmNeed} disabled={confirming} className="adr-btn-primary text-sm" data-testid="ffa-confirm">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" /> {confirming ? "Avvio..." : "Conferma e costruisci"}
                  </button>
                </div>
              )}
            </div>
          )}

          {status === "building" && (
            <div className="adr-card p-6 text-center">
              <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-[#0F3D6B]" />
              <h3 className="serif font-semibold mb-1">Sto costruendo l'applicazione</h3>
              <p className="text-sm text-[#555555]">Tipicamente 1-3 minuti. La pagina si aggiorna automaticamente.</p>
            </div>
          )}

          {(status === "preview_ready" || status === "delivered") && (
            <>
              <div className="adr-card p-5">
                <div className="adr-overline mb-3">Anteprima e consegna</div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={openPreview} className="adr-btn-secondary text-sm" data-testid="ffa-preview-btn">
                    <ExternalLink className="w-4 h-4 inline mr-1" /> Apri anteprima
                  </button>
                  <button onClick={download} className="adr-btn-primary text-sm" data-testid="ffa-download-btn">
                    <Download className="w-4 h-4 inline mr-1" /> Scarica pacchetto v{appData.current_version}
                  </button>
                </div>
              </div>

              <div className="adr-card p-5">
                <div className="adr-overline mb-3">Itera con linguaggio naturale</div>
                <form onSubmit={iterate} className="space-y-2">
                  <textarea value={iterText} onChange={(e) => setIterText(e.target.value)} placeholder='Es. "Quando carico la foto vorrei anche scrivere una nota di max 200 caratteri"' className="adr-input min-h-[80px]" data-testid="ffa-iter-input" />
                  <button type="submit" disabled={iterating || !iterText.trim()} className="adr-btn-outline text-sm" data-testid="ffa-iter-send">
                    <RotateCcw className="w-4 h-4 inline mr-1" /> {iterating ? "Avvio..." : "Avvia iterazione"}
                  </button>
                </form>
              </div>
            </>
          )}

          {status === "failed" && (
            <div className="adr-card p-5 border-l-4" style={{ borderLeftColor: "#C8102E", background: "rgba(200,16,46,0.04)" }}>
              <div className="text-sm font-semibold text-[#C8102E] mb-1">Costruzione fallita</div>
              <div className="text-sm">{appData.error_message || "Errore generico."}</div>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#555555] mt-8 leading-relaxed">
        L'applicazione generata è una bozza. Prima di usarla in produzione fai revisionare codice e configurazione a un tecnico qualificato.
      </div>
    </div>
  );
}
